import { useEffect, useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, X, Calendar, CalendarDays, Copy, ShieldCheck } from "lucide-react";

const DAY_LABELS = ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"];

const CATEGORY_CHIPS = [
  "All",
  "General Wellness",
  "Weight Management",
  "Diabetes",
  "Cholesterol / Heart Health",
  "Elderly",
  "Digestive Health",
  "Constipation",
  "Gas / Bloating",
  "PCOS",
  "Fatty Liver",
  "Sports Nutrition",
  "Vegetarian",
  "Family / Lifestyle",
];

const DISCLAIMER = "This nutrition template is intended for individualized dietary planning. Medical conditions, medications, allergies, kidney function, swallowing ability and other clinical factors should be reviewed by a qualified healthcare professional before finalizing the plan.";

export default function TemplatesPage() {
  const { me } = useOutletContext();
  const canEdit = ["owner", "admin", "dietitian"].includes(me?.user?.role);
  const [templates, setTemplates] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("All");
  const [seedTemplate, setSeedTemplate] = useState(null); // for Duplicate
  const load = () => api.get("/app/templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/app/templates/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const duplicate = (t) => {
    // Open the New Template modal pre-filled with t's content; user edits + saves as new
    setSeedTemplate({
      name: `${t.name} (copy)`,
      description: t.description || "",
      tags: (t.tags || []).join(", "),
      type: t.type || "single",
      meals: t.meals && t.meals.length ? JSON.parse(JSON.stringify(t.meals)) : [{ time: "", name: "", items: [""], notes: "" }],
      days: t.days && t.days.length ? JSON.parse(JSON.stringify(t.days)) : Array.from({ length: 7 }, () => ({ meals: [{ time: "", name: "", items: [""], notes: "" }], notes: "" })),
    });
    setShowNew(true);
  };

  const filtered = useMemo(() => {
    if (filter === "All") return templates;
    return templates.filter((t) => (t.tags || []).some((tag) => tag.toLowerCase() === filter.toLowerCase()));
  }, [templates, filter]);

  return (
    <div>
      <div className="section-eyebrow">Diet templates</div>
      <h1 className="mt-1 font-display text-3xl leading-tight" style={{ color: "var(--primary)" }}>Templates</h1>
      <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
        Single-day and 7-day meal plans you can apply to clients in one tap
      </p>

      {canEdit && (
        <button data-testid="new-template" onClick={() => { setSeedTemplate(null); setShowNew(true); }} className="btn-primary mt-4 text-sm">
          <Plus size={14} /> New template
        </button>
      )}

      {/* Category filter chips */}
      <div className="mt-5 -mx-4 px-4 overflow-x-auto">
        <div className="flex gap-2 pb-2 min-w-max">
          {CATEGORY_CHIPS.map((c) => {
            const active = filter === c;
            return (
              <button
                key={c}
                data-testid={`cat-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => setFilter(c)}
                className="text-xs font-bold px-3 py-1.5 rounded-full border-2 whitespace-nowrap"
                style={{
                  background: active ? "var(--primary)" : "var(--bg)",
                  color: active ? "var(--bg)" : "var(--primary)",
                  borderColor: active ? "var(--primary)" : "var(--border-subtle)",
                }}
              >{c}</button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {filtered.length === 0 && (
          <div className="card-soft p-6 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
            No templates in this category yet.
          </div>
        )}
        {filtered.map((t) => {
          const type = t.type || "single";
          const isWeekly = type === "weekly";
          const dayCount = isWeekly ? (t.days || []).length : (t.meals || []).length;
          return (
            <div key={t.id} data-testid={`template-${t.id}`} className="card-soft p-5">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg" style={{ color: "var(--primary)" }}>{t.name}</div>
                    <span
                      className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1"
                      style={{
                        background: isWeekly ? "var(--yellow)" : "var(--bg-paper)",
                        color: "var(--primary)",
                      }}
                    >
                      {isWeekly ? <CalendarDays size={10} /> : <Calendar size={10} />}
                      {isWeekly ? "7-Day" : "Single-day"}
                    </span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{(t.tags || []).join(" · ") || "—"}</div>
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <button onClick={() => duplicate(t)} data-testid={`dup-${t.id}`} className="text-xs font-bold" title="Duplicate" style={{ color: "var(--red)" }}>
                      <Copy size={14} />
                    </button>
                    <button onClick={() => del(t.id)} className="text-red-600" data-testid={`del-${t.id}`}><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
              {t.description && <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>{t.description}</p>}
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer font-bold" style={{ color: "var(--red)" }}>
                  {isWeekly ? `${dayCount} days` : `${dayCount} meals`} — view
                </summary>
                <div className="mt-3">
                  {isWeekly ? (
                    <div className="space-y-3">
                      {(t.days || []).map((d, di) => (
                        <div key={di} className="p-3 rounded-xl" style={{ background: "var(--bg-paper)" }}>
                          <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--primary)" }}>
                            {DAY_LABELS[di] || `Day ${di + 1}`}
                          </div>
                          <div className="mt-2 space-y-2">
                            {(d.meals || []).map((m, i) => (
                              <div key={i}>
                                <div className="font-bold text-xs" style={{ color: "var(--primary)" }}>{m.time || ""} · {m.name}</div>
                                <ul className="text-xs">{(m.items || []).map((it, j) => <li key={j}>• {it}</li>)}</ul>
                                {m.notes && <div className="text-[11px] italic mt-0.5" style={{ color: "var(--text-secondary)" }}>{m.notes}</div>}
                              </div>
                            ))}
                            {(d.meals || []).length === 0 && (
                              <div className="text-xs italic" style={{ color: "var(--text-secondary)" }}>No meals for this day</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(t.meals || []).map((m, i) => (
                        <div key={i} className="p-2 rounded-lg" style={{ background: "var(--bg-paper)" }}>
                          <div className="font-bold text-xs" style={{ color: "var(--primary)" }}>{m.time} · {m.name}</div>
                          <ul className="mt-1 text-xs">{(m.items || []).map((it, j) => <li key={j}>• {it}</li>)}</ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            </div>
          );
        })}
      </div>

      {/* Medical safety disclaimer */}
      <div className="mt-8 p-4 rounded-xl text-xs flex items-start gap-2" style={{ background: "var(--bg-paper)", border: "1px solid var(--border-subtle)" }}>
        <ShieldCheck size={14} className="flex-shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
        <div style={{ color: "var(--text-secondary)" }}>
          <span className="font-bold" style={{ color: "var(--primary)" }}>Medical note: </span>
          {DISCLAIMER}
        </div>
      </div>

      {showNew && <NewTemplateModal seed={seedTemplate} onClose={() => { setShowNew(false); setSeedTemplate(null); }} onCreated={() => { setShowNew(false); setSeedTemplate(null); load(); }} />}
    </div>
  );
}

const emptyMeal = () => ({ time: "", name: "", items: [""], notes: "" });
const emptyDay = () => ({ meals: [emptyMeal()], notes: "" });

function NewTemplateModal({ seed, onClose, onCreated }) {
  const [type, setType] = useState(seed?.type || "single"); // single | weekly
  const [f, setF] = useState({
    name: seed?.name || "",
    description: seed?.description || "",
    tags: seed?.tags || "",
  });
  const [meals, setMeals] = useState(seed?.meals || [emptyMeal()]);
  const [days, setDays] = useState(() => {
    if (seed?.days && seed.days.length) {
      // Ensure 7 days
      const d = seed.days.slice(0, 7);
      while (d.length < 7) d.push(emptyDay());
      return d.map((x) => ({ meals: x.meals && x.meals.length ? x.meals : [emptyMeal()], notes: x.notes || "" }));
    }
    return Array.from({ length: 7 }, () => emptyDay());
  });
  const [activeDay, setActiveDay] = useState(0);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Single-day meal helpers
  const updM = (i, patch) => setMeals(meals.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const upItem = (mi, ii, val) => updM(mi, { items: meals[mi].items.map((it, idx) => idx === ii ? val : it) });

  // Weekly day helpers
  const dayMeals = days[activeDay]?.meals || [];
  const updDayMeal = (i, patch) => {
    const next = [...days];
    next[activeDay] = {
      ...next[activeDay],
      meals: next[activeDay].meals.map((m, idx) => idx === i ? { ...m, ...patch } : m),
    };
    setDays(next);
  };
  const upDayItem = (mi, ii, val) => updDayMeal(mi, { items: dayMeals[mi].items.map((it, idx) => idx === ii ? val : it) });
  const addDayMeal = () => {
    const next = [...days];
    next[activeDay] = { ...next[activeDay], meals: [...next[activeDay].meals, emptyMeal()] };
    setDays(next);
  };
  const removeDayMeal = (i) => {
    const next = [...days];
    next[activeDay] = { ...next[activeDay], meals: next[activeDay].meals.filter((_, idx) => idx !== i) };
    setDays(next);
  };
  const copyPrev = () => {
    if (activeDay === 0) return;
    const next = [...days];
    // Deep-copy from previous day
    next[activeDay] = JSON.parse(JSON.stringify(days[activeDay - 1]));
    setDays(next);
    toast.success(`Copied from ${DAY_LABELS[activeDay - 1]}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const tags = f.tags.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        name: f.name,
        description: f.description,
        tags,
        type,
      };
      if (type === "single") {
        payload.meals = meals.map((m) => ({ ...m, items: m.items.filter(Boolean) }));
      } else {
        payload.days = days.map((d, di) => ({
          day_index: di,
          meals: (d.meals || []).map((m) => ({ ...m, items: (m.items || []).filter(Boolean) })),
          notes: d.notes || null,
        }));
      }
      await api.post("/app/templates", payload);
      toast.success("Template created");
      onCreated();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="w-full max-w-md card-soft p-6 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <div className="section-eyebrow">New</div>
            <h3 className="font-display text-2xl mt-1" style={{ color: "var(--primary)" }}>Diet template</h3>
          </div>
          <button onClick={onClose}><X /></button>
        </div>

        {/* Type toggle */}
        <div className="mt-4 flex gap-1 p-1 rounded-full" style={{ background: "var(--bg-paper)" }}>
          {[
            { key: "single", label: "Single-day", icon: Calendar },
            { key: "weekly", label: "7-day", icon: CalendarDays },
          ].map((opt) => {
            const Icon = opt.icon;
            const active = type === opt.key;
            return (
              <button
                key={opt.key}
                data-testid={`template-type-${opt.key}`}
                type="button"
                onClick={() => setType(opt.key)}
                className="flex-1 flex items-center justify-center gap-1 py-2 rounded-full text-xs font-bold"
                style={{
                  background: active ? "var(--primary)" : "transparent",
                  color: active ? "var(--bg)" : "var(--primary)",
                }}
              >
                <Icon size={12} /> {opt.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <Input placeholder="Template name *" value={f.name} onChange={upd("name")} required className="h-11 rounded-xl" />
          <Input placeholder="Short description" value={f.description} onChange={upd("description")} className="h-11 rounded-xl" />
          <Input placeholder="Tags (comma-separated)" value={f.tags} onChange={upd("tags")} className="h-11 rounded-xl" />

          {type === "single" ? (
            <>
              <div className="text-xs uppercase tracking-widest font-bold mt-4" style={{ color: "var(--text-secondary)" }}>Meals</div>
              {meals.map((m, i) => (
                <div key={i} className="p-3 rounded-xl space-y-2" style={{ background: "var(--bg-paper)" }}>
                  <div className="flex gap-2">
                    <Input value={m.time} onChange={(e) => updM(i, { time: e.target.value })} placeholder="Time" className="h-9 rounded-lg w-24 text-sm" />
                    <Input value={m.name} onChange={(e) => updM(i, { name: e.target.value })} placeholder="Meal name" className="h-9 rounded-lg flex-1 text-sm" />
                  </div>
                  {m.items.map((it, ii) => (
                    <Input key={ii} value={it} onChange={(e) => upItem(i, ii, e.target.value)} placeholder={`Item ${ii + 1}`} className="h-9 rounded-lg text-sm" />
                  ))}
                  <button type="button" onClick={() => updM(i, { items: [...m.items, ""] })} className="text-xs font-bold" style={{ color: "var(--red)" }}>+ item</button>
                </div>
              ))}
              <button type="button" onClick={() => setMeals([...meals, emptyMeal()])} className="btn-ghost w-full text-sm">+ Add meal</button>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest font-bold mt-4" style={{ color: "var(--text-secondary)" }}>
                7-day planner — build each day
              </div>
              <div className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-2">
                {DAY_LABELS.map((lbl, di) => {
                  const active = di === activeDay;
                  const filled = (days[di].meals || []).some((m) => m.name || (m.items || []).some(Boolean));
                  return (
                    <button
                      key={di}
                      type="button"
                      data-testid={`tmpl-day-${di}`}
                      onClick={() => setActiveDay(di)}
                      className="flex-shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-bold border-2 min-w-[54px]"
                      style={{
                        background: active ? "var(--primary)" : "var(--bg)",
                        color: active ? "var(--bg)" : "var(--primary)",
                        borderColor: active ? "var(--primary)" : "var(--border-subtle)",
                      }}
                    >
                      {lbl.replace("Day ", "D")}
                      <div className="mt-0.5">
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: filled ? (active ? "var(--yellow)" : "var(--red)") : (active ? "rgba(255,255,255,0.3)" : "var(--border-subtle)") }} />
                      </div>
                    </button>
                  );
                })}
              </div>

              {activeDay > 0 && (
                <button type="button" onClick={copyPrev} className="text-xs font-bold" style={{ color: "var(--red)" }}>
                  ↩ Copy from {DAY_LABELS[activeDay - 1]}
                </button>
              )}

              {dayMeals.map((m, i) => (
                <div key={i} className="p-3 rounded-xl space-y-2" style={{ background: "var(--bg-paper)" }}>
                  <div className="flex gap-2">
                    <Input value={m.time} onChange={(e) => updDayMeal(i, { time: e.target.value })} placeholder="Time" className="h-9 rounded-lg w-24 text-sm" />
                    <Input value={m.name} onChange={(e) => updDayMeal(i, { name: e.target.value })} placeholder="Meal name" className="h-9 rounded-lg flex-1 text-sm" />
                    <button type="button" onClick={() => removeDayMeal(i)} className="text-red-600"><Trash2 size={14} /></button>
                  </div>
                  {(m.items || []).map((it, ii) => (
                    <Input key={ii} value={it} onChange={(e) => upDayItem(i, ii, e.target.value)} placeholder={`Item ${ii + 1}`} className="h-9 rounded-lg text-sm" />
                  ))}
                  <button type="button" onClick={() => updDayMeal(i, { items: [...(m.items || []), ""] })} className="text-xs font-bold" style={{ color: "var(--red)" }}>+ item</button>
                </div>
              ))}
              <button type="button" onClick={addDayMeal} className="btn-ghost w-full text-sm">+ Add meal to {DAY_LABELS[activeDay]}</button>
            </>
          )}

          <button data-testid="submit-template" disabled={loading} className="btn-primary w-full mt-2 disabled:opacity-60">
            {loading ? "…" : `Create ${type === "weekly" ? "7-day " : ""}template`}
          </button>
        </form>
      </div>
    </div>
  );
}
